import * as React from 'react'

import { durations } from '~ui/styleGuide/styles'

type TOptions = RequiredChildren & {
  /**
   * whether or not should the content be expanded & rendered
   */
  isOpen: boolean
  /**
   * minimal size the the closed container should have
   */
  closedSize?: number
  /**
   * duration of the opening/closing animation
   */
  animationTimeMs?: number
}

const NO_DELAY = 0

/**
 * hook for animating React component height
 * - support for keeping the components rendered while closing
 * - adapts to children height (to be able to animate height: `auto`)
 */
export const useAnimatedHeight = ({
  isOpen,
  children,
  closedSize = 0,
  animationTimeMs = durations.basic,
}: TOptions) => {
  const refUpdateShouldRenderTimeout = React.useRef<Nullable<number>>(null)
  const refContent = React.useRef<Nullable<HTMLDivElement>>(null)

  const [containerHeight, setContainerHeight] = React.useState(0)
  const [shouldRenderContent, setShouldRenderContent] = React.useState(isOpen)

  // update container height either to the content height (if open) or to the closedSize prop (if closed)
  const updateContainer = React.useCallback(
    (open: boolean) => {
      if (!refContent.current) {
        return
      }

      const contentHeight = open ? refContent.current.scrollHeight : closedSize
      setContainerHeight(contentHeight)
    },
    [closedSize],
  )

  // update the container height whenever any of the relevant variables changes
  React.useEffect(() => {
    updateContainer(isOpen)
  }, [children, refContent, isOpen, shouldRenderContent, updateContainer])

  // react to content resize
  React.useLayoutEffect(() => {
    const contentElement = refContent.current

    if (!contentElement) return

    try {
      const resizeObserver = new ResizeObserver(() => updateContainer(isOpen))
      resizeObserver.observe(contentElement)

      return () => {
        resizeObserver.disconnect()
      }
    } catch {}

    return
  }, [isOpen, updateContainer])

  // react to window resize (children layout might’ve shifted)
  // ? is this still necessary?
  React.useLayoutEffect(() => {
    window.addEventListener(`resize`, () => updateContainer(isOpen))

    return () => {
      window.removeEventListener(`resize`, () => updateContainer(isOpen))
    }
  }, [isOpen, updateContainer])

  // update the shouldRenderContent state:
  //  - immediately when isOpen changes to true,
  //  - after animation duration when isOpen changes to false (to keep the content rendered while closing animation is playing)
  React.useEffect(() => {
    if (refUpdateShouldRenderTimeout.current) {
      clearTimeout(refUpdateShouldRenderTimeout.current)
    }

    const delay = isOpen ? NO_DELAY : animationTimeMs

    refUpdateShouldRenderTimeout.current = window.setTimeout(() => {
      setShouldRenderContent(isOpen)
    }, delay)
  }, [animationTimeMs, isOpen, updateContainer])

  return { refContent, containerHeight, shouldRenderContent }
}
